import { FC, useEffect, useRef } from 'react'
import { SectionArticleChapterProps } from '@portfolio/models'
import { PortableText, SectionWrapper } from '@portfolio/components'
import { styled } from '@portfolio/styles'
import {
  useRegisterChapterHeading,
  useSetChapterHeadingVisibility,
  useUnregisterChapterHeading,
} from '@portfolio/article'
import { SectionQuote } from '../SectionQuote/SectionQuote'
import { SectionIconWithTextList } from '../SectionIconWithTextList/SectionIconWithTextList'
import { SectionSingleMediaWithDescription } from '../SectionSingleMediaWithDescription/SectionSingleMediaWithDescription'
import { SectionVideoPlayer } from '../SectionVideoPlayer/SectionVideoPlayer'
import { SectionQuoteWithVideo } from '../SectionQuoteWithVideo/SectionQuoteWithVideo'
import { SectionStats } from '../SectionStats/SectionStats'
import { SectionExternalArtifact } from '../SectionExternalArtifact/SectionExternalArtifact'
import { SectionMedia } from '../SectionMedia/SectionMedia'

const StyledSectionWrapper = styled(SectionWrapper, {
  maxWidth: '$richTextWidth',
  containerSpace: 'medium',
})

const ChapterTitle = styled('h2', {
  marginBottom: '.75em',
  marginTop: '1.5em',
  projectFont: 'heading05',
})

export const SectionArticleChapter: FC<SectionArticleChapterProps> = ({
  content,
  title,
}) => {
  const registerChapterHeading = useRegisterChapterHeading()
  const setChapterHeadingVisibility = useSetChapterHeadingVisibility()
  const unregisterChapterHeading = useUnregisterChapterHeading()
  const chapterTitleRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (!chapterTitleRef.current || !title) return
    const chapterHeadingElement = chapterTitleRef.current

    registerChapterHeading(title)

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0]

        setChapterHeadingVisibility(title, entry.isIntersecting)
      },
      {
        root: null,
        threshold: 0,
      },
    )

    observer.observe(chapterHeadingElement)

    return () => {
      observer.disconnect()
      unregisterChapterHeading(title)
    }
  }, [
    registerChapterHeading,
    setChapterHeadingVisibility,
    title,
    unregisterChapterHeading,
  ])

  return (
    <StyledSectionWrapper id={title} type="NO_MARGIN">
      {title && (
        <ChapterTitle ref={chapterTitleRef} data-article-chapter-title={title}>
          {title}
        </ChapterTitle>
      )}
      {content && (
        <PortableText
          value={content}
          additionalComponents={{
            types: {
              sectionQuote: (props) => {
                return <SectionQuote {...props.value} />
              },
              sectionIconWithTextList: (props) => {
                return <SectionIconWithTextList {...props.value} />
              },
              sectionSingleMediaWithDescription: (props) => {
                return <SectionSingleMediaWithDescription {...props.value} />
              },
              sectionVideoPlayer: (props) => {
                return <SectionVideoPlayer {...props.value} />
              },
              sectionQuoteWithVideo: (props) => {
                return <SectionQuoteWithVideo {...props.value} />
              },
              sectionStats: (props) => {
                return <SectionStats {...props.value} />
              },
              sectionExternalArtifact: (props) => {
                return <SectionExternalArtifact {...props.value} />
              },
              sectionMedia: (props) => {
                return <SectionMedia {...props.value} />
              },
            },
          }}
        />
      )}
    </StyledSectionWrapper>
  )
}
